<template>
  <!-- Sign in, in the shared signed-out frame (world map left, form right). -->
  <AuthShell>
    <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors }">
      <h2 class="auth-title">{{ t('auth.signInTitle') }}</h2>
      <p class="auth-sub">{{ t('auth.signInSub') }}</p>

      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.email') }}</span>
        <Field name="email" v-slot="{ field }">
          <input v-bind="field" type="email" name="email" autocomplete="username" placeholder="name@company.com" />
        </Field>
        <span v-if="errors.email" class="auth-error">{{ errors.email }}</span>
      </label>

      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.password') }}</span>
        <Field name="password" v-slot="{ field }">
          <input v-bind="field" type="password" name="password" autocomplete="current-password" />
        </Field>
        <span v-if="errors.password" class="auth-error">{{ errors.password }}</span>
      </label>

      <template v-if="requiresRecaptcha">
        <Field v-model="reCaptchaV2Token" name="recaptcha" type="hidden" />
        <div class="auth-captcha" :class="{ 'is-error': Boolean(errors.recaptcha) }">
          <vue-recaptcha :sitekey="accountStore.reCaptchaSiteKey" size="normal" :hl="locale === 'zh-CN' ? 'zh-CN' : 'en'" :key="locale" theme="dark"
            :loading-timeout="recaptchaTimeout" @verify="recaptchaVerified"
            @expire="recaptchaExpired" @error="recaptchaError">
          </vue-recaptcha>
        </div>
        <span v-if="errors.recaptcha" class="auth-error" style="margin: -10px 0 12px;">{{ errors.recaptcha }}</span>
      </template>

      <button class="auth-btn" type="submit" :disabled="Boolean(loading)">{{ loading ? t('auth.signingIn') : t('auth.logIn') }}</button>

      <div v-if="name !== 'proxywarehouse'" class="auth-links">
        <router-link v-if="!chatHost" to="/register">{{ t('auth.createAccount') }}</router-link>
        <router-link to="/forgotpass">{{ t('auth.forgotPasswordLink') }}</router-link>
      </div>
    </Form>
  </AuthShell>
</template>

<script setup>
import { t, locale } from '@/i18n';

import { Form, Field } from 'vee-validate';
import AuthShell from '../AuthShell.vue';
import * as Yup from 'yup';
import vueRecaptcha from 'vue3-recaptcha2';
import { ref } from "vue"
import { storeToRefs } from "pinia";
import { useAccountStore, useAlertStore } from '@/stores';
const alertStore = useAlertStore();
const accountStore = useAccountStore();
const {loading} = storeToRefs(accountStore)
const name = process.env.VUE_APP_VUE_NAME
const chatHost = window.location.hostname.startsWith('chat.');   // staff chat app: no sign-ups
// Enable the verified standard-v2 CAPTCHA for production login.
const requiresRecaptcha = !!process.env.VUE_APP_VUE_RECAPTCHA_V2_SITE_KEY   // reCAPTCHA once this site has its own key (.env)
const schema = Yup.object().shape({
  email: Yup.string().required(() => t('auth.v.emailRequired')).email(() => t('auth.v.emailInvalid')),
  password: Yup.string().required(() => t('auth.v.passwordRequired')).min(8, () => t('auth.v.passwordMin')),
  ...(requiresRecaptcha ? { recaptcha: Yup.string().required(() => t('auth.v.captcha')) } : {}),
});

const recaptchaTimeout = ref(30000);
const reCaptchaV2Token = ref('');
function recaptchaVerified(response) {
  reCaptchaV2Token.value = response;
}
function recaptchaExpired() {
  reCaptchaV2Token.value = '';
}
function recaptchaError(reason) {
  reCaptchaV2Token.value = '';
  alertStore.error(t('auth.recaptchaError'));
}

async function onSubmit(values) {
  const accountStore = useAccountStore();
  const { email, password } = values;
  await accountStore.signin(email, password, reCaptchaV2Token.value);
}
</script>
